/**
Service for fetching the boreholes nearest to the given coordinate.

Example output:
[
  {
    "BoreholeId": 779581,
    "BoreholeNumber": "GI_14018836_2",
    "X": 574598.5929637103,
    "Y": 6228472.113549921,
    "Epsg": 0,
    "DistanceToBorehole": 46.64895697526009,
    "DatasourceId": 1
  }
]
*/

import { GeoWebServiceClient } from './GeoWebServiceClient'

/**
 * The object type returned by the nearest borehole webservice.
 */
export interface NearestBoreholeObject {
  BoreholeId: number
  BoreholeNumber: string
  X: number
  Y: number
  Espg: number
  DistanceToBorehole: number
  DatasourceId: number
}

/**
 * Web service for fetching the boreholes near to a given map coordinate.
 * The following parameters are required: 'X' & 'Y' ( @see setCoordinate ),
 * 'MaxDistance' ( @see setDistance ), and 'MaxBoreholeNumber' ( @see setLimit ).
 */
export class NearestBoreholes extends GeoWebServiceClient {
  static get URL (): string { return 'https://data.geo.dk/api/v3/borehole/GetNearestBorehole' }

  /**
   * Constructor.
   * @param token The Geo auth token.
   */
  constructor (token: string) {
    const defaultParams = {
      MaxBoreholeNumber: '1',
      MaxDistance: '50',
      HideWhiteBorehole: '1'
    }
    super(NearestBoreholes.URL, token, defaultParams)
  }

  /**
   * [Required] Setter for the coordinate.
   * @param coordinate The map coordinate to use in the lookup operation.
   */
  setCoordinate (coordinate: number[]): void {
    if (coordinate.length !== 2) {
      throw new Error('Argument error: coordinate must have exactly two entries.')
    }
    this.addParam('X', coordinate[0].toString())
    this.addParam('Y', coordinate[1].toString())
  }

  /**
   * Setter for the maximum search distance. Default value is 50m.
   * @param distance The max distance in meters. The service searches this far
   * away from the given point when looking for boreholes.
   */
  setDistance (distance: number): void {
    this.addParam('MaxDistance', distance.toString())
  }

  /**
   * Setter for the search limit. Default is 1.
   * @param limit The maximum number of boreholes to return.
   */
  setLimit (limit: number): void {
    this.addParam('MaxBoreholeNumber', limit.toString())
  }

  /**
   * Validates that required params are given.
   */
  private validateParams (): void {
    if (!this.hasParam('X') || !this.hasParam('Y')) {
      throw new Error('Parameter mismatch: The search coordinate has not been set.')
    }
  }

  /**
   * Calls the web service.
   * @returns A promise bearing the nearest borehole information objects fetched.
   */
  async fetchBoreHoles (): Promise<NearestBoreholeObject[]> {
    this.validateParams()
    const response: Response = await fetch(this.url(), { headers: { Authorization: `Bearer ${this.token}` } })
    if (!response.ok) {
      throw new Error('Got invalid HTTP response.')
    }

    const responseData = await response.json()
    return responseData
  }
}
